import { Search, Star, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import type { AssetFolder } from '@/types'

type TypeFilter = 'all' | 'image' | 'video' | 'reference'

interface Props {
  search: string
  onSearchChange: (v: string) => void
  typeFilter: TypeFilter
  onTypeFilterChange: (v: TypeFilter) => void
  folderFilter: string
  onFolderFilterChange: (v: string) => void
  statusFilter: string
  onStatusFilterChange: (v: string) => void
  starredOnly: boolean
  onStarredOnlyChange: (v: boolean) => void
  folders: AssetFolder[]
  onClear: () => void
  hasFilters: boolean
  folderSelectClassName?: string
}

const TYPE_OPTIONS: { value: TypeFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'image', label: 'Images' },
  { value: 'video', label: 'Videos' },
  { value: 'reference', label: 'References' },
]

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'pending', label: 'Pending' },
  { value: 'processing', label: 'Processing' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'skipped', label: 'Skipped' },
]

export function AssetLibraryFilterBar({
  search,
  onSearchChange,
  typeFilter,
  onTypeFilterChange,
  folderFilter,
  onFolderFilterChange,
  statusFilter,
  onStatusFilterChange,
  starredOnly,
  onStarredOnlyChange,
  folders,
  onClear,
  hasFilters,
  folderSelectClassName,
}: Props) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* Search */}
      <div className="relative max-w-xs w-full sm:w-auto">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
        <Input
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search assets..."
          className="pl-8 h-8 text-sm"
          aria-label="Search assets"
        />
      </div>

      {/* Type toggle group */}
      <div className="flex items-center gap-1" role="group" aria-label="Filter by type">
        {TYPE_OPTIONS.map((opt) => (
          <Button
            key={opt.value}
            variant={typeFilter === opt.value ? 'default' : 'outline'}
            size="sm"
            className="h-8 text-xs px-3"
            onClick={() => onTypeFilterChange(opt.value)}
            aria-pressed={typeFilter === opt.value}
          >
            {opt.label}
          </Button>
        ))}
      </div>

      {/* Folder filter — hidden on desktop when sidebar is present */}
      <Select value={folderFilter} onValueChange={onFolderFilterChange}>
        <SelectTrigger
          className={cn('h-8 text-sm w-[160px]', folderSelectClassName)}
          aria-label="Filter by folder"
        >
          <SelectValue placeholder="All Folders" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Folders</SelectItem>
          {folders.map((folder) => (
            <SelectItem key={folder.id} value={String(folder.id)}>
              {folder.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Starred filter — click to toggle */}
      <Button
        variant={starredOnly ? 'default' : 'outline'}
        size="sm"
        className="h-8 text-xs px-3 gap-1"
        onClick={() => onStarredOnlyChange(!starredOnly)}
        aria-pressed={starredOnly}
        aria-label="Show starred assets only"
      >
        <Star
          className={cn('h-3.5 w-3.5', starredOnly && 'fill-current')}
        />
        Starred
      </Button>

      {/* Status filter */}
      <Select value={statusFilter} onValueChange={onStatusFilterChange}>
        <SelectTrigger
          className={cn('h-8 text-sm w-[150px]')}
          aria-label="Filter by analysis status"
        >
          <SelectValue placeholder="All Status" />
        </SelectTrigger>
        <SelectContent>
          {STATUS_OPTIONS.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Clear filters */}
      {hasFilters && (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 text-xs text-muted-foreground hover:text-foreground px-2 gap-1"
          onClick={onClear}
          aria-label="Clear all filters"
        >
          <X className="h-3.5 w-3.5" />
          Clear
        </Button>
      )}
    </div>
  )
}
